import {
  ChangeEvent,
  FocusEvent,
  Component,
  InputHTMLAttributes,
  useState,
  RefObject,
  MutableRefObject
} from 'react';
import cn from 'classnames';
import Icon from 'components/Shared/Icon/index';
import { IconCheckmark } from 'components/Shared/Icon';
import Spinner from 'components/Shared/Loader';
import * as LegacyConfig from './inputLabelConfig/themes/default';
import * as RD3Config from './inputLabelConfig/themes/BGH';
import styles from './themes/BGH/input.scss';
import { IconPlus } from '../Icon/themes/BGH-RD3/iconPlus';

export type OnChangeHandler = (e: ChangeEvent<HTMLInputElement>, newValue?: string) => void;

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  innerRef?: RefObject<HTMLInputElement> | MutableRefObject<HTMLInputElement>;
  invalid?: boolean;
  valid?: boolean;
  loading?: boolean;
  className?: string;
  unitClassName?: string;
  unit?: string;
  onChange?: OnChangeHandler;
  min?: number;
  max?: number;
  onClear?: () => void;
}

export class Input extends Component<InputProps> {
  public render() {
    const {
      innerRef,
      invalid,
      valid,
      className,
      unitClassName,
      loading,
      type = 'text',
      onChange,
      onBlur,
      onClear,
      ...rest
    } = this.props;

    const unit = Boolean(this.props.unit && this.props.value) && (
      <span className={cn(styles.unit, unitClassName)} style={{ left: this.leftPad(true) }}>
        {this.props.unit}
      </span>
    );

    return (
      <div
        className={cn(styles.input, className, {
          [styles.invalid]: invalid,
          [styles.valid]: valid
        })}
      >
        <input
          ref={innerRef}
          type={type}
          className={styles.inputField}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          {...rest}
        />
        {invalid && <Icon icon="icon-warning" inline className={styles.inputIcon} />}
        {valid && (
          <IconCheckmark inline size="small" className={cn(styles.inputIcon, styles.check)} />
        )}
        {loading && <Spinner loading className={cn(styles.inputIcon, styles.spinner)} />}
        {unit}
        {onClear && (
          <button
            className={cn(styles.resetBtn, {
              [styles.hide]: !this.props.value || invalid || loading
            })}
            onClick={onClear}
          >
            <IconPlus className={styles.resetIcon} />
          </button>
        )}
      </div>
    );
  }

  private handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    if (this.props.type === 'number') {
      if (!/^(-?0[.,]?\d*)|-?[1-9]\d*/.test(newValue)) {
        const target = e.currentTarget;
        window.setTimeout(() => (target.value = ''), 0);
      }
    }
    this.props.onChange && this.props.onChange(e, newValue);
  };

  private handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (this.props.type === 'number') {
      let newValue = Number.parseFloat(e.currentTarget.value);
      if (Number.isNaN(newValue) && this.props.min && !Number.isNaN(this.props.min)) {
        newValue = this.props.min;
      }
      const min = this.props.min || newValue;
      const max = this.props.max || newValue;
      newValue = Math.max(min, Math.min(max, newValue));
      e.currentTarget.value = newValue.toString();
      this.props.onChange && this.props.onChange(e, newValue.toString());
    }
    this.props.onBlur && this.props.onBlur(e);
  };

  private leftPad = (experimentRunning: boolean) => {
    const { PADDING, CHAR_SIZE } = experimentRunning ? RD3Config : LegacyConfig;
    const value = this.props.value || '';
    return `${PADDING + CHAR_SIZE * (typeof value !== 'number' ? value.length : value)}px`;
  };
}

export interface LabeledInputProps extends InputProps {
  label?: string;
  tooltip?: string;
}

export const LabeledInput = ({ className, label, tooltip, ...input }: LabeledInputProps) => {
  const [hasFocus, setFocus] = useState(false);
  const shrinkTooltip =
    hasFocus || (input.value !== undefined && input.value.toString().length > 0);
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    input.onFocus && input.onFocus(e);
    setFocus(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    input.onBlur && input.onBlur(e);
    setFocus(false);
  };
  return (
    <div className={cn(styles.labelInput, className)}>
      {(label || tooltip) && (
        <span className={cn(styles.label, { [styles.shrink]: shrinkTooltip })}>
          {label && <label dangerouslySetInnerHTML={{ __html: label }} />}
        </span>
      )}
      <Input {...input} onFocus={handleFocus} onBlur={handleBlur} />
    </div>
  );
};
